'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  MAPPED_TRANSLATION,
  VoucherStatus,
  VoucherType,
  giftCardPdfPublicPath,
  toApiAbsoluteUrl,
  isPlainPackPurchase,
  public_env,
} from '@cfixe-monorepo/shared';
import type { GiftCardConfirmationResponseObject } from '@cfixe-monorepo/shared';
import { fetchGiftCardConfirmation } from '../../website/api/professional-api';

const T =
  (MAPPED_TRANSLATION['fr-FR'] as unknown as Record<string, Record<string, string>>)['GIFT_CARD_PUBLIC_PAGE'] ?? {};
const t = (key: string): string => T[key] ?? key;

const MAX_ATTEMPTS = 40; // ~2 min à 3s

export interface GiftCardConfirmationProps {
  paymentUuid: string;
  /** Appelé une fois quand le bon est confirmé (status != WAITING_PAYMENT) — utilisé par le widget pour émettre un event. */
  onReady?: (voucherUuid: string) => void;
}

/**
 * Confirmation d'achat de carte cadeau : poll le statut du bon jusqu'à activation puis affiche un
 * récapitulatif (code + contenu acheté + destinataire + lien PDF), dans le style « réservation
 * confirmée » du tunnel (`bt-confirmed`). Partagé entre la page hébergée et la fin du tunnel inline.
 */
export function GiftCardConfirmation({ paymentUuid, onReady }: GiftCardConfirmationProps): React.JSX.Element {
  const [data, setData] = useState<GiftCardConfirmationResponseObject | null>(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const readyFiredRef = useRef(false);

  useEffect((): (() => void) => {
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const poll = async (): Promise<void> => {
      attempts += 1;
      try {
        const res = await fetchGiftCardConfirmation(paymentUuid);
        if (cancelled) return;
        setData(res);
        const paid = res.status !== VoucherStatus.WAITING_PAYMENT;
        if (paid && !readyFiredRef.current) {
          readyFiredRef.current = true;
          onReadyRef.current?.(res.voucher_uuid);
        }
        const waitingForPdf = !res.pdf_ready;
        if ((!paid || waitingForPdf) && attempts < MAX_ATTEMPTS) {
          timer = setTimeout((): void => void poll(), 3000);
        }
      } catch {
        if (cancelled) return;
        if (attempts < MAX_ATTEMPTS) timer = setTimeout((): void => void poll(), 3000);
        else setError(true);
      }
    };

    void poll();
    return (): void => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [paymentUuid]);

  if (error) {
    return (
      <div className="bt-step">
        <div className="bt-confirmed">
          <div className="bt-confirmed__message">{t('ERROR_GENERIC')}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bt-step">
        <div className="bt-loading">
          <div className="bt-spinner" />
        </div>
      </div>
    );
  }

  const paid = data.status !== VoucherStatus.WAITING_PAYMENT;
  if (!paid) {
    return (
      <div className="bt-step">
        <div className="bt-confirmed">
          <div className="bt-loading">
            <div className="bt-spinner" />
          </div>
          <div className="bt-confirmed__message">{t('CONFIRM_PENDING')}</div>
        </div>
      </div>
    );
  }

  const pdfUrl = toApiAbsoluteUrl(public_env.API_ENDPOINT, giftCardPdfPublicPath(data.voucher_uuid));
  const items = data.items ?? [];
  const showAmount = data.type === VoucherType.AMOUNT && Boolean(data.amount_formatted);
  // Forfait acheté pour soi (pack non-cadeau) : confirmation « achat » neutre (il a son PDF comme tous les bons).
  const isPlainPack = isPlainPackPurchase(data);
  // Variante de wording calculée une fois (titre + description dérivent du même suffixe → jamais désync).
  const wordingSuffix = data.is_gift ? '_GIFT' : isPlainPack ? '_PACK' : '';
  const code = data.unique_code;

  const handleCopy = (): void => {
    if (!code) return;
    void navigator.clipboard
      ?.writeText(code)
      .then((): void => {
        setCopied(true);
        setTimeout((): void => setCopied(false), 2000);
      })
      .catch((): void => undefined);
  };

  return (
    <div className="bt-step gc-confirm">
      {/* En-tête « confirmé » (mirror du tunnel de réservation). */}
      <div className="bt-confirmed">
        <div className="bt-confirmed__icon">✓</div>
        <div className="bt-confirmed__title">{t(`CONFIRM_TITLE${wordingSuffix}`)}</div>
        <div className="bt-confirmed__message">{t(`CONFIRM_DESC${wordingSuffix}`)}</div>
      </div>

      {/* Action principale (téléchargement du PDF) remontée sous l'en-tête pour rester visible.
          Tous les bons ont un PDF, forfait acheté pour soi compris. */}
      {data.pdf_ready ? (
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="bt-cta gc-confirm__download">
          {t('CONFIRM_DOWNLOAD')}
        </a>
      ) : (
        <p className="bt-confirmed__message gc-confirm__pdf-pending">{t('CONFIRM_PDF_PENDING')}</p>
      )}

      {/* Récapitulatif (carte façon mini-panier). */}
      <div className="bt-summary__card gc-confirm__card">
        {code && (
          <div className="gc-confirm__code-block">
            <span className="bt-summary__row-label--sub">{t('CONFIRM_CODE')}</span>
            <span className="bt-gift-code">{code}</span>
            <button type="button" className="gc-confirm__copy" onClick={handleCopy} aria-label={t('CONFIRM_COPY_CODE')}>
              <CopyIcon />
              {copied ? t('CONFIRM_CODE_COPIED') : t('CONFIRM_COPY_CODE')}
            </button>
          </div>
        )}

        {(showAmount || data.pack_name || items.length > 0) && (
          <>
            <div className="bt-summary__divider" />
            <div className="bt-summary__title">{t('CONFIRM_RECAP_TITLE')}</div>

            {showAmount && (
              <div className="bt-summary__row">
                <span className="bt-summary__row-label">{t('AMOUNT_LINE_LABEL')}</span>
                <span className="bt-summary__row-value">{String(data.amount_formatted)}</span>
              </div>
            )}
            {data.pack_name && (
              <div className="bt-summary__row">
                <span className="bt-summary__row-label">{data.pack_name}</span>
              </div>
            )}
            {items.map(
              (it, i): React.JSX.Element => (
                <div key={`${it.name}-${i}`} className="bt-summary__row">
                  <span className="bt-summary__row-label bt-summary__row-label--sub">
                    {it.quantity > 1 ? `${it.quantity} × ` : ''}
                    {it.name}
                  </span>
                </div>
              ),
            )}
          </>
        )}

        {data.is_gift && (data.gift_recipient_name || data.gift_message) && (
          <>
            <div className="bt-summary__divider" />
            {data.gift_recipient_name && (
              <div className="bt-summary__row">
                <span className="bt-summary__row-label bt-summary__row-label--sub">
                  🎁 {t('FOR_RECIPIENT')} {data.gift_recipient_name}
                </span>
              </div>
            )}
            {data.gift_message && (
              <div className="bt-summary__row">
                <span className="gc-confirm__message">« {data.gift_message} »</span>
              </div>
            )}
          </>
        )}

        {data.expiration_date_formatted && (
          <>
            <div className="bt-summary__divider" />
            <div className="bt-summary__row">
              <span className="bt-summary__row-label bt-summary__row-label--sub">{t('CONFIRM_EXPIRES')}</span>
              <span className="bt-summary__row-value bt-summary__row-value--sub">
                {String(data.expiration_date_formatted)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CopyIcon(): React.JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
